import React, { useRef, useState } from 'react';
import { AnimatePresence, motion, useInView } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, ChevronDownIcon } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useEscaCopy } from '../../i18n/escaCopy';
import { track } from '../../utils/analytics';
export function ClassicExpertise() {
  const {
    lang
  } = useLanguage();
  const c = useEscaCopy(lang);
  const [activeIdx, setActiveIdx] = useState(0);
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const active = c.expertise.categories[activeIdx];
  const onCta = (e: React.MouseEvent) => {
    e.preventDefault();
    track('expertise_cta_click', {
      variant: 'classic'
    });
    document.getElementById('contact')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section id="expertise" className="relative py-20 lg:py-28 bg-white overflow-hidden" data-id="element-607" data-mp="8mli8za1kqb">
      <div className="absolute inset-0 pointer-events-none" data-id="element-608" data-mp="x1iufz4bd97d">
        <div className="absolute top-1/3 left-0 w-[450px] h-[450px] rounded-full bg-primary-50/40 blur-3xl hidden sm:block" data-id="element-609" data-mp="111tc491gxo22" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-cyan-50/40 blur-3xl hidden sm:block" data-id="element-610" data-mp="160xheol9v1n4" />
        <div className="absolute inset-0 opacity-[0.018]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, rgba(15,23,42,1) 1px, transparent 0)`,
        backgroundSize: '48px 48px'
      }} data-id="element-611" data-mp="1atgxptbcgavm" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-612" data-mp="1orf9qs26zv7y">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-14 lg:mb-16" data-id="element-613" data-mp="1tr06cvtw3rhi">
          <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-614" data-mp="3viwmyjuwzrj5">
            {lang === 'EN' ? 'Expertise' : 'Спеціалізація'}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight max-w-4xl mx-auto" data-id="element-615" data-mp="xp3cp2r74hn1">
            {c.expertise.title}
          </h2>
          <p className="mt-4 text-lg text-navy-500 max-w-2xl mx-auto leading-relaxed" data-id="element-616" data-mp="16p86ga3gvhiq">
            {c.expertise.lead}
          </p>
        </motion.div>

        {/* Desktop */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.12
      }} className="hidden lg:grid grid-cols-[20rem_1fr] gap-8" data-id="element-617" data-mp="1lkecuy6h5icl">
          <div className="space-y-1.5" data-id="element-618" data-mp="1qcz2dnicl9ab">
            {c.expertise.categories.map((cat, i) => {
            const isActive = i === activeIdx;
            return <button key={cat.name} onClick={() => setActiveIdx(i)} aria-pressed={isActive} className={`w-full text-left px-5 py-3.5 rounded-xl text-[0.9375rem] font-semibold transition-colors duration-200 ${isActive ? 'bg-gradient-to-r from-primary-600 to-primary-500 text-white shadow-lg shadow-primary-600/20' : 'text-navy-600 hover:bg-navy-50'}`} data-id="element-619" data-mp="1zdxaagvwq0y6" data-mpi={i}>
                  {cat.name}
                </button>;
          })}
          </div>

          <div className="rounded-2xl bg-navy-50/60 border border-navy-200 p-8 min-h-[26rem]" data-id="element-620" data-mp="250ftyq6zn228">
            <AnimatePresence mode="wait" data-id="element-621" data-mp="1qbjpt481y96w">
              <motion.div key={active.name} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -6
            }} transition={{
              duration: 0.22
            }} data-id="element-622" data-mp="1qcuvj9l25r0n">
                <h3 className="text-xl font-bold text-navy-900" data-id="element-623" data-mp="1lc6urw711vt8">
                  {active.name}
                </h3>
                <ul className="mt-6 grid grid-cols-2 gap-x-8 gap-y-1" data-id="element-624" data-mp="1e7fi7ixgtynj">
                  {active.roles.map(role => <li key={role} className="flex items-start gap-2.5 py-2 text-[0.9375rem] text-navy-700" data-id="element-625" data-mp="19afj5rz19w5x">
                      <CheckIcon className="flex-shrink-0 w-4 h-4 mt-1 text-primary-500" data-id="element-626" data-mp="zj2vlck9ps5p" />
                      {role}
                    </li>)}
                </ul>
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Mobile accordion */}
        <div className="lg:hidden space-y-2.5" data-id="element-627" data-mp="lkl9fja0gnpw">
          {c.expertise.categories.map((cat, i) => {
          const isOpen = openIdx === i;
          return <div key={cat.name} className="rounded-2xl bg-white border border-navy-200 overflow-hidden" data-id="element-628" data-mp="2fych4d8s4xip" data-mpi={i}>
                <button type="button" onClick={() => setOpenIdx(isOpen ? null : i)} aria-expanded={isOpen} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left" data-id="element-629" data-mp="2awwsycbb2aw3">
                  <span className="text-[0.9375rem] font-bold text-navy-900" data-id="element-630" data-mp="2s8vd0y7jfjff">
                    {cat.name}
                  </span>
                  <ChevronDownIcon className={`flex-shrink-0 w-4 h-4 text-primary-500 transition-transform duration-200 ease-out ${isOpen ? 'rotate-180' : ''}`} data-id="element-631" data-mp="20h5qanr8295s" />
                </button>
                <AnimatePresence initial={false} data-id="element-632" data-mp="29gj1plywp7t5">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.25
              }} className="overflow-hidden" data-id="element-633" data-mp="2ehq8pit862yr">
                      <ul className="px-5 pb-5 space-y-1" data-id="element-634" data-mp="309rnb4cjjk8h">
                        {cat.roles.map(role => <li key={role} className="flex items-start gap-2.5 py-1.5 text-sm text-navy-600" data-id="element-635" data-mp="3a64d3r4ytdxy">
                            <CheckIcon className="flex-shrink-0 w-4 h-4 mt-0.5 text-primary-500" data-id="element-636" data-mp="3556vbfnvalwz" />
                            {role}
                          </li>)}
                      </ul>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.24
      }} className="mt-12 lg:mt-14 p-7 lg:p-9 rounded-2xl bg-navy-50/60 border border-navy-200 grid lg:grid-cols-[1fr_auto] gap-6 lg:gap-12 items-center" data-id="element-637" data-mp="2mfs0i2yryt11">
          <div data-id="element-638" data-mp="980q3g1ebs4p">
            <h3 className="text-lg lg:text-xl font-bold text-navy-900" data-id="element-639" data-mp="e0ae2qiyai4c">
              {c.expertise.footTitle}
            </h3>
            <p className="mt-2 text-[0.9375rem] text-navy-500 leading-relaxed max-w-2xl" data-id="element-640" data-mp="3vi3cdzw4f1pe">
              {c.expertise.footText}
            </p>
          </div>
          <a href="#contact" onClick={onCta} className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 text-base font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-500 rounded-xl hover:from-primary-700 hover:to-primary-600 transition-colors shadow-lg shadow-primary-600/25 whitespace-nowrap" data-id="element-641" data-mp="s0bpv10r79yl">
            {c.expertise.footCta}
            <ArrowRightIcon className="w-4 h-4 transition-transform duration-200 ease-out group-hover:translate-x-1" data-id="element-642" data-mp="33q490sgngwmq" />
          </a>
        </motion.div>
      </div>
    </section>;
}